import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { openWhatsApp } from "@/lib/whatsapp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Users, CheckCircle2, XCircle, LogOut, MessageCircle, Coffee, CalendarDays } from "lucide-react";

function Stat({ label, value, sub, icon: Icon, testId }) {
  return (
    <div className="bg-white border border-border p-6 md:p-8 fade-up" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs tracking-[0.25em] uppercase text-muted-foreground">{label}</div>
          <div className="mt-3 md:mt-4 font-serif text-4xl md:text-5xl tracking-tight">{value}</div>
          {sub && <div className="mt-2 text-sm text-muted-foreground">{sub}</div>}
        </div>
        <Icon className="w-5 h-5 text-primary" aria-hidden="true" />
      </div>
    </div>
  );
}

export default function DashboardHome() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ownerPhone, setOwnerPhone] = useState("");
  const [onBreak, setOnBreak] = useState([]);
  const [tick, setTick] = useState(0);
  const [wastage, setWastage] = useState(null);
  const [wastageDate, setWastageDate] = useState(() => new Date().toISOString().slice(0, 10));

  useEffect(() => {
    api.get("/inventory/daily-wastage", { params: { on_date: wastageDate } })
      .then(({ data }) => setWastage(data))
      .catch(() => {});
  }, [wastageDate]);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: today }, { data: settings }, { data: br }] = await Promise.all([
          api.get("/attendance/today"),
          api.get("/settings/public"),
          api.get("/attendance/on-break"),
        ]);
        setData(today);
        setOwnerPhone(settings.owner_phone || "");
        setOnBreak(br);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Live-refresh the break bar every 30 seconds
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const { data: br } = await api.get("/attendance/on-break");
        setOnBreak(br);
        setTick((t) => t + 1);
      } catch (err) {
        // Non-critical: the next tick will retry; log for observability
        if (process.env.NODE_ENV !== "production") console.warn("on-break refresh failed", err);
      }
    }, 30000);
    return () => clearInterval(id);
  }, []);

  // Recompute elapsed minutes every 30s (also used as key for react)
  const elapsedNow = (isoStart) => {
    try {
      const start = new Date(isoStart).getTime();
      return Math.max(0, Math.round((Date.now() - start) / 60000));
    } catch {
      return 0;
    }
  };

  const notifyAbsentees = async () => {
    try {
      const [{ data: emps }, { data: today }] = await Promise.all([
        api.get("/employees"),
        api.get("/attendance/today"),
      ]);
      const presentIds = new Set(today.records.map((r) => r.employee_id));
      const absent = emps
        .filter((e) => e.active && !presentIds.has(e.id))
        .map((e) => `- ${e.name} (${e.role})`)
        .join("\n");
      const message =
        `Royal Wow Meals — Absentees on ${today.date}\n\n` +
        (absent || "Everyone is present today.") +
        `\n\nTotal absent: ${today.absent} of ${today.total_employees}`;
      openWhatsApp(ownerPhone, message);
    } catch {
      openWhatsApp(ownerPhone, "Royal Wow Meals: could not fetch absentee list.");
    }
  };

  return (
    <div data-testid="dashboard-home">
      <div className="fade-up flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-muted-foreground">Today</p>
          <h1 className="font-serif text-3xl sm:text-5xl tracking-tight mt-2">Kitchen at a glance</h1>
          <p className="mt-3 text-muted-foreground">
            {data?.date && new Date(data.date).toLocaleDateString(undefined, {
              weekday: "long",
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </p>
        </div>
        {!loading && data && data.absent > 0 && (
          <Button
            onClick={notifyAbsentees}
            data-testid="notify-absentees-btn"
            className="rounded-full bg-emerald-600 text-white hover:bg-emerald-700"
          >
            <MessageCircle className="w-4 h-4 mr-2" aria-hidden="true" />
            Notify Owner on WhatsApp ({data.absent} absent)
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-10">
        <Stat label="Total staff" value={loading ? "—" : data.total_employees} icon={Users} testId="stat-total" />
        <Stat label="Present" value={loading ? "—" : data.present} icon={CheckCircle2} testId="stat-present" />
        <Stat label="Absent" value={loading ? "—" : data.absent} icon={XCircle} testId="stat-absent" />
        <Stat label="Checked out" value={loading ? "—" : data.checked_out} icon={LogOut} testId="stat-checked-out" />
      </div>

      {/* Daily stock-out vs closing return: wastage snapshot */}
      {wastage && (
        <section className="mt-10 bg-white border border-border p-6 fade-up" data-testid="wastage-panel">
          <div className="flex items-baseline justify-between flex-wrap gap-3 mb-4">
            <div>
              <p className="text-xs tracking-[0.3em] uppercase text-muted-foreground">Daily wastage</p>
              <h2 className="mt-1 font-serif text-2xl tracking-tight flex items-center gap-2 flex-wrap">
                Stock out vs returns
                <span className="inline-flex items-center gap-1 text-sm font-sans text-primary bg-primary/10 px-2 py-0.5 rounded-full" data-testid="wastage-date-label">
                  <CalendarDays className="w-3.5 h-3.5" />
                  {new Date(wastage.date + "T00:00:00").toLocaleDateString([], { weekday: "short", day: "numeric", month: "short", year: "numeric" })}
                </span>
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                What went to the kitchen minus what was cooked and what came back.
              </p>
              <div className="mt-3 flex items-center gap-2">
                <label htmlFor="wastage-date" className="text-xs uppercase tracking-widest text-muted-foreground">See day</label>
                <Input
                  id="wastage-date"
                  type="date"
                  value={wastageDate}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setWastageDate(e.target.value)}
                  data-testid="wastage-date-picker"
                  className="w-40 h-9"
                />
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Est. wastage qty</div>
              <div className="font-serif text-3xl text-primary" data-testid="wastage-total">
                {(wastage.total_wastage != null ? wastage.total_wastage : (wastage.total_out - wastage.total_close)).toFixed(1)}
              </div>
              <div className="text-[10px] text-muted-foreground">
                {wastage.total_out.toFixed(1)} out - {(wastage.total_used || 0).toFixed(1)} used - {wastage.total_close.toFixed(1)} back
              </div>
            </div>
          </div>
          {wastage.items_tracked === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-sm">
              No stock movements recorded on {wastage.date}. Try picking another date above.
            </div>
          ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm" data-testid="wastage-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground">
                  <th className="py-2 pr-4 font-normal">Item</th>
                  <th className="py-2 pr-4 font-normal text-right">Out</th>
                  <th className="py-2 pr-4 font-normal text-right">Utilised</th>
                  <th className="py-2 pr-4 font-normal text-right">Returned</th>
                  <th className="py-2 pr-4 font-normal text-right">Wastage</th>
                  <th className="py-2 pr-4 font-normal text-right">%</th>
                </tr>
              </thead>
              <tbody>
                {wastage.rows.slice(0, 8).map((r) => (
                  <tr key={r.item_id} className="border-t border-border">
                    <td className="py-2 pr-4 font-medium">{r.item_name} <span className="text-xs text-muted-foreground">({r.unit})</span></td>
                    <td className="py-2 pr-4 text-right">{r.out_qty}</td>
                    <td className="py-2 pr-4 text-right text-purple-700">{r.used_qty || 0}</td>
                    <td className="py-2 pr-4 text-right text-muted-foreground">{r.close_qty}</td>
                    <td className={`py-2 pr-4 text-right font-serif ${r.wastage > 0 ? "text-primary" : "text-emerald-700"}`}>
                      {r.wastage}
                    </td>
                    <td className={`py-2 pr-4 text-right text-xs ${r.wastage_pct > 50 ? "text-red-600" : r.wastage_pct > 20 ? "text-amber-700" : "text-emerald-700"}`}>
                      {r.wastage_pct}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {wastage.rows.length > 8 && (
              <p className="mt-2 text-xs text-muted-foreground text-right">
                {wastage.rows.length - 8} more items — see the Inventory tab
              </p>
            )}
          </div>
          )}
        </section>
      )}

      {onBreak.length > 0 && (
        <div className="mt-6 bg-white border border-[#D4AF37] p-6 fade-up" data-testid="break-bar">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-primary">
            <Coffee className="w-4 h-4" aria-hidden="true" />
            Currently on break · {onBreak.length}
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" key={tick}>
            {onBreak.map((b) => {
              const mins = elapsedNow(b.break_start);
              const startStr = new Date(b.break_start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
              return (
                <div key={b.employee_id} className="border border-border p-3 flex items-center justify-between bg-secondary/40" data-testid={`on-break-${b.employee_id}`}>
                  <div>
                    <div className="font-medium">{b.employee_name}</div>
                    <div className="text-xs text-muted-foreground">since {startStr}</div>
                  </div>
                  <div className="font-serif text-2xl text-primary">{mins}<span className="text-xs text-muted-foreground ml-1">min</span></div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <section className="mt-14 fade-up">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="font-serif text-2xl tracking-tight">Today&apos;s activity</h2>
          <span className="text-xs text-muted-foreground">{data?.records?.length || 0} entries</span>
        </div>

        <div className="bg-white border border-border">
          {loading ? (
            <div className="p-10 text-center text-muted-foreground">Loading…</div>
          ) : data.records.length === 0 ? (
            <div className="p-14 text-center">
              <p className="font-serif text-2xl">No punches yet today.</p>
              <p className="text-muted-foreground mt-2">Open the kiosk and let the team check in.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full min-w-[520px]" data-testid="today-activity-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground">
                  <th className="px-6 py-4 font-normal">Employee</th>
                  <th className="px-6 py-4 font-normal">Check in</th>
                  <th className="px-6 py-4 font-normal">Check out</th>
                  <th className="px-6 py-4 font-normal text-right">Hours</th>
                </tr>
              </thead>
              <tbody>
                {data.records.map((r) => (
                  <tr key={r.id} className="border-t border-border">
                    <td className="px-6 py-4 font-medium">{r.employee_name}</td>
                    <td className="px-6 py-4 text-muted-foreground">
                      {r.check_in ? new Date(r.check_in).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
                    </td>
                    <td className="px-6 py-4 text-muted-foreground">
                      {r.check_out ? new Date(r.check_out).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : (
                        <span className="text-primary">On shift</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right font-serif text-lg">{r.hours || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
